import { useCallback, useMemo } from "react";

import useFetchLatestReport from "./queries/useFetchLatestReport";
import useFetchPublishedTrackerBlockerConfig from "./queries/useFetchPublishedTrackerBlockerConfig";
import { BlockerStatus, WebTracker, WebTrackerResolution } from "./types";

export type UseTrackerBlockingStalenessResult = {
  isLoading: boolean;
  isError: boolean;
  isResolutionPublished: (resolution: WebTrackerResolution) => boolean;
  staleness: BlockerStatus | null;
  trackersResolvedWithBlocking: WebTracker[];
};

export default function useTrackerBlockingStaleness(): UseTrackerBlockingStalenessResult {
  const {
    data: latestReport,
    isLoading: isLatestReportLoading,
    isError: isLatestReportError,
  } = useFetchLatestReport();

  const {
    data: publishedTrackerBlockerConfig,
    isLoading: isPublishedTrackerBlockerConfigLoading,
    isError: isPublishedConfigError,
  } = useFetchPublishedTrackerBlockerConfig();

  const publishedBlocklistRegexes = useMemo(() => {
    if (publishedTrackerBlockerConfig === undefined) {
      return [];
    }

    const blocklist =
      publishedTrackerBlockerConfig?.blockerConfig?.blocklist ?? [];

    return blocklist.map((blockedDomainRegexStr) => {
      return new RegExp(blockedDomainRegexStr);
    });
  }, [publishedTrackerBlockerConfig]);

  const isResolutionPublished = useCallback(
    (resolution: WebTrackerResolution) => {
      if (publishedBlocklistRegexes.length === 0) {
        return false;
      }

      for (const regex of publishedBlocklistRegexes) {
        if (regex.test(resolution.trackerDomain)) {
          return true;
        }
      }

      return false;
    },
    [publishedBlocklistRegexes]
  );

  const trackersResolvedWithBlocking: WebTracker[] = useMemo(() => {
    if (latestReport === undefined) {
      return [];
    }

    return latestReport.trackers.filter(
      (t) => t.resolution?.resolutionType === "tracker_blocked"
    );
  }, [latestReport]);

  const isLoading =
    isLatestReportLoading || isPublishedTrackerBlockerConfigLoading;
  const isError = isLatestReportError || isPublishedConfigError;

  const staleness = useMemo(() => {
    if (isLoading || isError) {
      return null;
    }
    if (latestReport === undefined) {
      return BlockerStatus.UP_TO_DATE;
    }
    if (publishedTrackerBlockerConfig?.blockerConfig?.enabled === false) {
      return BlockerStatus.DISABLED;
    }

    for (const t of trackersResolvedWithBlocking) {
      if (!isResolutionPublished(t.resolution!)) {
        return BlockerStatus.CHANGES_PENDING;
      }
    }

    return BlockerStatus.UP_TO_DATE;
  }, [
    isLoading,
    isError,
    latestReport,
    publishedTrackerBlockerConfig?.blockerConfig?.enabled,
    trackersResolvedWithBlocking,
    isResolutionPublished,
  ]);

  return {
    isLoading,
    isError,
    isResolutionPublished,
    staleness,
    trackersResolvedWithBlocking,
  };
}
